"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Image from "next/image";
import { useRef } from "react";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

gsap.registerPlugin(ScrollTrigger);

export function ReportEcoSystemSection({
  pageImageMap,
}: {
  pageImageMap?: SitePageImageMap;
}) {
  const ecosystemImg = resolvePageImage("free-digital-report-ecosystem", pageImageMap);

  const scope = useRef<HTMLElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const maskRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const viewport = viewportRef.current;
      const mask = maskRef.current;
      if (!mask || !viewport) return;

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          isDesktop: "(min-width: 1024px)",
          isMobile: "(max-width: 1023px)",
        },
        (context) => {
          const { reduceMotion } = context?.conditions as {
            reduceMotion: boolean;
          };

          if (reduceMotion) {
            gsap.set(mask, {
              width: "100%",
              height: "100%",
              borderRadius: "1.9rem",
            });
          } else {
            gsap.fromTo(
              mask,
              { width: 500, height: 500, borderRadius: "100%" },
              {
                width: "100%",
                height: "100%",
                borderRadius: "1.9rem",
                ease: "none",
                scrollTrigger: {
                  trigger: viewport,
                  start: "center 90%",
                  end: "center 70%",
                  scrub: 1.3,
                  invalidateOnRefresh: true,
                },
              },
            );
          }
        },
      );

      const syncVars = () => {
        viewport.style.setProperty("--screen-w", `${viewport.offsetWidth}px`);
        viewport.style.setProperty("--screen-h", `${viewport.offsetHeight}px`);
      };
      syncVars();
      window.addEventListener("resize", syncVars);

      return () => {
        window.removeEventListener("resize", syncVars);
        mm.revert();
      };
    },
    { scope, dependencies: [] },
  );

  return (
    <section
      id="digital-report"
      ref={scope}
      className="relative min-h-[1200px] scroll-mt-20 overflow-visible bg-[#1b2631] pt-16 pb-20 font-sans text-white md:pt-20 md:scroll-mt-24"
      aria-labelledby="free-report-ecosystem-heading"
    >
      <div className="site-content pt-12 pb-0 md:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[680px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#8fa8bf] md:text-base">
              Creative social media campaigns
         
         
            </p>
            <h2
              id="free-report-ecosystem-heading"
              className="mt-4 text-4xl leading-[0.8em] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Stop the endless scrolling and{" "}
              <span className="text-[#ff5c00]"> own the moment</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className="text-4xl font-medium leading-none text-[#ff5c00] md:text-5xl">
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-white/90" />
            <p className="mt-6 text-base leading-relaxed text-white/95 md:text-lg">
              Great creativity stops thumbs mid-scroll
              and earns the click. It makes them pause,
              laugh, and think, &quot;Hang on&quot;– then follow
              the idea through to your website instead
              of mindlessly scrolling on.
         
            </p>
          </div>
        </div>
      </div>

      <div className="site-content relative mt-6 sm:h-0 md:mt-16">
        <div className="relative left-0 right-0 top-0 z-10 w-full md:left-1/2 md:right-auto md:w-[min(30rem,76vw)] md:-translate-x-1/2 lg:w-[min(42rem,72vw)]">
          <div className="max-md:rounded-2xl max-md:p-0 sm:max-md:rounded-3xl sm:max-md:p-2.5 rounded-[2.5rem] p-2.5 md:rounded-[2.75rem] md:p-3 lg:rounded-[3rem] lg:p-3.5">
            <div
              ref={viewportRef}
              className="relative aspect-9/17 w-full overflow-hidden max-md:rounded-[1.35rem] rounded-[1.85rem] md:rounded-[2.125rem] lg:rounded-4xl"
            >
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  ref={maskRef}
                  className="flex items-center justify-center overflow-hidden will-change-[width,height,border-radius]"
                >
                  <div
                    className="relative shrink-0 [&_img]:pointer-events-none"
                    style={{
                      width: "var(--screen-w, 100%)",
                      height: "var(--screen-h, 100%)",
                    }}
                  >
                    <Image
                      src={ecosystemImg.src}
                      alt={ecosystemImg.alt}
                      fill
                      priority
                      sizes="(max-width: 767px) 90vw, (max-width: 1023px) 76vw, 72vw"
                      className="object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
